import { createReducer, on } from '@ngrx/store';

import { isPresent } from '@fcom/core/utils/utils';
import { ServiceSelections, TravelerService } from '@fcom/dapi';
import { Category } from '@fcom/dapi/api/models/category';

import { getOverlappingCategoriesForCategory, isSeatBundle } from '../../../modules/ancillaries/utils/category.utils';
import * as SeatMapActions from '../../actions/seat-map.actions';
import * as ServicesActions from '../../actions/services.actions';
import { CommonBookingState } from '../../store.interface';
import { setSeatSelectionForFlight } from '../seat-map.reducer';
import { asSegmentId } from '../../../utils/flight.utils';

const asArray = (selection: TravelerService | TravelerService[] | undefined): TravelerService[] =>
  Array.isArray(selection) ? selection : selection ? [selection] : [];

const seatBundleCategories = Object.values(Category).filter((category) => isSeatBundle(category));

const clearSeatBundleSelections = (
  state: CommonBookingState,
  flightId: string,
  travelerId: string
): CommonBookingState => {
  if (!state.services) {
    return state;
  }

  const selectionsWithSeatBundlesCleared = seatBundleCategories.reduce((allSelections, category) => {
    const selectionsForCategory = allSelections[category as keyof ServiceSelections];
    return {
      ...allSelections,
      [category]: { ...selectionsForCategory, [flightId]: { ...selectionsForCategory?.[flightId], [travelerId]: [] } },
    };
  }, state.services.selections ?? {});

  return { ...state, services: { ...state.services, selections: selectionsWithSeatBundlesCleared } };
};

const getSeatNumberAttachedToOverlappingService = (
  state: CommonBookingState,
  category: Category,
  flightId: string,
  travelerId: string
): string | undefined =>
  getOverlappingCategoriesForCategory(category)
    .flatMap((overlapping) =>
      asArray(state.services?.selections?.[overlapping as keyof ServiceSelections]?.[flightId]?.[travelerId])
    )
    .find(({ seatNumber }) => seatNumber)?.seatNumber;

const restoreSeatAttachedToOverlappingService = (
  state: CommonBookingState,
  category: Category,
  fragmentId: string,
  travelerId: string,
  incoming: TravelerService[]
): CommonBookingState => {
  // if selections contain a seat number, we don't need to restore anything
  if (incoming.some(({ seatNumber }) => seatNumber)) {
    return state;
  }

  const flightId = asSegmentId(fragmentId);
  const seatNumber = getSeatNumberAttachedToOverlappingService(state, category, flightId, travelerId);
  const seat = state.seatMap?.seatmaps?.[flightId]?.seatMap?.seats?.find((s) => s.seatNumber === seatNumber);

  return seat ? { ...state, seatMap: setSeatSelectionForFlight(state.seatMap, flightId, travelerId, seat) } : state;
};

export const seatSelectionReconciliationReducer = createReducer<CommonBookingState>(
  {},
  on(ServicesActions.setSelectionForFragmentForTraveler, (state, { category, fragmentId, travelerId, selection }) =>
    state.seatMap && isSeatBundle(category) && isPresent(selection?.seatNumber)
      ? { ...state, seatMap: setSeatSelectionForFlight(state.seatMap, asSegmentId(fragmentId), travelerId, null) }
      : restoreSeatAttachedToOverlappingService(state, category, fragmentId, travelerId, asArray(selection))
  ),
  on(ServicesActions.setSelectionsForFragment, (state, { category, fragmentId, selections }) =>
    Object.entries(selections)
      // Overlaps are only cleared for travelers this action writes a selection for.
      .filter(([, incoming]) => incoming?.length)
      .reduce(
        (updated, [travelerId, incoming]) =>
          restoreSeatAttachedToOverlappingService(updated, category, fragmentId, travelerId, incoming),
        state
      )
  ),
  on(SeatMapActions.setSeatForFlight, (state, { flightId, travelerId }) =>
    clearSeatBundleSelections(state, flightId, travelerId)
  )
);
